"use client";

import React, { useContext, useState } from "react";
import { Transition } from "@headlessui/react";
import classNames from "classnames";
import dynamic from "next/dynamic";

import StoreLocatorContext from "@/context/StoreLocatorContext";
import { RetailType } from "@/custom-types/Store";
import { MapData, Store } from "@/custom-types/StoreLocator";
import useIsMobileDetection from "@/hooks/useIsMobileDetection";

import MarkerSprites from "./MarkerSprites/MarkerSprites";

const MapOverlays = dynamic(() => import("./MapOverlays/MapOverlays"), {
  ssr: false,
});

const MapComponent = dynamic(() => import("./MapComponent"), {
  ssr: false,
});

const MapPlaceholder = dynamic(() => import("./MapPlaceholder"), {
  ssr: true,
});

const MapSection: React.FC<{
  initMapData: MapData;
  retailType: RetailType;
  spotlightStores: Store[];
  stores: Store[];
}> = ({ initMapData, retailType, spotlightStores, stores }) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const { fullDrawerEnabled } = useContext(StoreLocatorContext);
  const isMobile = useIsMobileDetection();
  const showFinderDefaultMobileTest = isMobile && fullDrawerEnabled;
  return (
    <div
      className={classNames(
        "fixed bg-white md:bottom-0",
        "md:left-[var(--stores-desktop-listing-width)] md:top-[var(--header-height)]",
        "inset-[var(--header-height)_0_var(--stores-mobile-bottom-touchpoint)_0]",
      )}
    >
      {!showFinderDefaultMobileTest && (
        <Transition show={!mapLoaded}>
          <div className="absolute top w-full h-full transition duration-1000 data-[closed]:opacity-0">
            <MapPlaceholder />
          </div>
        </Transition>
      )}
      <div
        className={classNames(
          "h-full w-full transition delay-300 duration-1000",
          {
            "opacity-0": !mapLoaded,
          },
        )}
      >
        <MapComponent
          retailType={retailType}
          stores={stores}
          initMapData={initMapData}
          setMapLoaded={setMapLoaded}
          mapLoaded={mapLoaded}
        />
      </div>
      {spotlightStores.length > 0 && (
        <MapOverlays spotlightStores={spotlightStores} />
      )}
      <MarkerSprites />
    </div>
  );
};

export default MapSection;
